import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { reviewApi, errorMessage } from '../api';
import { isFieldVisible, isInputField } from '../engine';
import AnswerView from '../AnswerView';
import { READINESS_LABELS, formatDate } from '../ui';

/**
 * Printable copy of a submission. "Download PDF" opens this in a new tab and
 * the browser's print dialog saves it as a PDF.
 */
export default function ChallengeReviewPrint() {
    const { submissionId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        reviewApi.get(submissionId)
            .then((d) => {
                setData(d);
                document.title = `${d.challenge.title} — ${d.student.name}`;
                setTimeout(() => window.print(), 400);
            })
            .catch((err) => setError(errorMessage(err, 'Could not load this submission')));
    }, [submissionId]);

    if (error) return <p className="p-8 text-sm text-red-700">{error}</p>;
    if (!data) return <p className="p-8 text-sm text-ink-500">Preparing document…</p>;

    const { challenge, student, submission } = data;
    const answers = submission.answers || {};
    const score = submission.score;

    return (
        <div className="mx-auto max-w-3xl bg-white px-8 py-10 text-ink-950 print:px-0 print:py-0">
            <div className="mb-6 flex items-center justify-between print:hidden">
                <p className="text-sm text-ink-500">Use your browser's print dialog and choose "Save as PDF".</p>
                <button type="button" onClick={() => window.print()} className="btn-primary">Print / Save PDF</button>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-500">{challenge.stage_label}</p>
            <h1 className="mt-1 text-2xl font-bold">{challenge.title}</h1>
            <p className="mt-1 text-sm text-ink-700">
                {student.name}{student.cohort ? ` · ${student.cohort}` : ''} · {submission.submitted_at ? `Submitted ${formatDate(submission.submitted_at)}` : 'Not submitted'} · Status: {submission.status.replace('_', ' ')}
            </p>
            {score && (
                <p className="mt-2 text-sm">
                    <span className="font-semibold">AI readiness score:</span> {score.total} / {score.max} ({score.percent}%) · {READINESS_LABELS[score.level]}
                    {score.na_count ? ` · ${score.na_count} area(s) N/A` : ''}
                </p>
            )}
            {submission.mentor_feedback && (
                <p className="mt-2 whitespace-pre-line text-sm"><span className="font-semibold">Mentor feedback:</span> {submission.mentor_feedback}</p>
            )}

            {challenge.sections.filter((s) => (s.fields || []).some(isInputField)).map((section) => (
                <section key={section.key} className="mt-8 break-inside-avoid-page">
                    <h2 className="border-b border-ink-200 pb-1 text-lg font-bold">{section.key} · {section.title}</h2>
                    {section.fields.map((field, i) => {
                        if (field.type === 'heading') return <h3 key={`h${i}`} className="mt-4 text-sm font-bold text-ink-700">{field.title}</h3>;
                        if (!isInputField(field) || !isFieldVisible(field, answers)) return null;
                        const comments = submission.comments.filter((c) => c.field_key === field.key);
                        return (
                            <div key={field.key} className="mt-3 break-inside-avoid">
                                <p className="text-xs font-semibold text-ink-600">{field.label}</p>
                                <div className="mt-0.5"><AnswerView field={field} answer={answers[field.key]} files={submission.files.filter((f) => f.field_key === field.key)} print /></div>
                                {comments.map((c) => <p key={c.id} className="mt-1 text-xs italic text-ink-600">Mentor comment ({c.author_name}): {c.text}</p>)}
                            </div>
                        );
                    })}
                    {submission.comments.filter((c) => !c.field_key && c.section_key === section.key).map((c) => (
                        <p key={c.id} className="mt-2 text-xs italic text-ink-600">Mentor comment on this part ({c.author_name}): {c.text}</p>
                    ))}
                </section>
            ))}
        </div>
    );
}
